<template>
  <section class="metadata-field">
    <label :for="inputId">
      {{ label }}
    </label>

    <div class="metadata-entry-container">
      <div class="metadata-entry-row">
        <div class="metadata-input-shell">
          <div v-if="pills.length" class="metadata-pills">
            <button
              v-for="pill in pills"
              :key="`${pill.status}:${pill.key}`"
              type="button"
              class="metadata-pill"
              :class="`pill-${pill.status}`"
              :title="pillTitle(pill)"
              :aria-label="pillTitle(pill)"
              @click="handlePill(pill)"
            >
              <span
                v-if="pill.status === 'current' || pill.status === 'new'"
                class="pill-symbol"
                aria-hidden="true"
              >
                ×
              </span>

              <span v-else class="material-icons-outlined pill-status" aria-hidden="true">
                schedule
              </span>

              <span>{{ pill.value }}</span>
            </button>
          </div>

          <input
            :id="inputId"
            v-model="entryText"
            type="text"
            autocomplete="off"
            role="combobox"
            :aria-autocomplete="suggestions.length ? 'list' : undefined"
            :aria-controls="suggestions.length ? suggestionListId : undefined"
            :aria-expanded="suggestions.length ? suggestionsOpen : undefined"
            :placeholder="pills.length ? '' : placeholder"
            @focus="openSuggestions"
            @blur="closeSuggestions"
            @input="handleInput"
            @keydown.down.prevent="moveSuggestion(1)"
            @keydown.up.prevent="moveSuggestion(-1)"
            @keydown.enter.prevent="handleEnter"
            @keydown.esc="closeSuggestions"
          />
        </div>

        <button
          type="button"
          class="enter-button"
          :disabled="!canAdd"
          @click="addEntries"
        >
          {{ entryButtonLabel }}
        </button>
      </div>

      <div
        v-if="suggestionsOpen && filteredSuggestions.length"
        :id="suggestionListId"
        class="metadata-suggestions"
        role="listbox"
        :aria-label="`${label} suggestions`"
      >
        <button
          v-for="(suggestion, index) in filteredSuggestions"
          :key="suggestion"
          type="button"
          class="metadata-suggestion"
          :class="{ highlighted: index === highlightedSuggestionIndex }"
          role="option"
          :aria-selected="index === highlightedSuggestionIndex"
          tabindex="-1"
          @mousedown.prevent="selectSuggestion(suggestion)"
          @mouseenter="highlightedSuggestionIndex = index"
        >
          <span class="material-icons-outlined" aria-hidden="true">sell</span>
          <span>{{ suggestion }}</span>
        </button>
      </div>
    </div>

    <span
      v-if="isCreatingNewValue"
      class="new-value-alert"
      role="status"
    >
      <span class="material-icons-outlined" aria-hidden="true">info</span>
      <span>{{ newValueAlert }}</span>
    </span>

    <span v-if="suggestionsLoading" class="suggestion-status">
      Loading saved {{ itemNamePlural }}…
    </span>

    <span v-else-if="suggestionsError" class="suggestion-status suggestion-error">
      {{ suggestionsError }} You can still enter a new {{ itemName }}.
    </span>

    <span class="field-hint">
      {{ hint }} Enter one phrase or paste comma-separated values.
    </span>

  </section>
</template>

<script>
import { computed, ref } from "vue";
import { normalizeSearchMetadata } from "@/models/searchMetadata";

export default {
  name: "LitigationSearchMetadataInput",
  props: {
    modelValue: { type: Array, default: () => [] },
    originalValues: { type: Array, default: () => [] },
    label: { type: String, required: true },
    placeholder: { type: String, default: "" },
    hint: { type: String, default: "" },
    suggestions: { type: Array, default: () => [] },
    suggestionsLoading: { type: Boolean, default: false },
    suggestionsError: { type: String, default: "" },
    itemName: { type: String, default: "tag" },
    addButtonLabel: { type: String, default: "Enter" },
    newValueButtonLabel: { type: String, default: "Create and Add" },
    confirmNewValues: { type: Boolean, default: false },
    highlightNewValues: { type: Boolean, default: false },
    creatorMode: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],

  setup(props, { emit }) {
    const entryText = ref("");
    const pendingValues = ref([]);
    const suggestionsOpen = ref(false);
    const highlightedSuggestionIndex = ref(-1);
    const inputId = `litigation-metadata-${Math.random().toString(36).slice(2)}`;
    const suggestionListId = `${inputId}-suggestions`;
    const comparisonKey = value => String(value ?? "").trim().toLocaleLowerCase();
    const itemNamePlural = computed(() =>
      props.itemName.endsWith("y")
        ? `${props.itemName.slice(0, -1)}ies`
        : `${props.itemName}s`
    );

    const currentValues = computed(() => normalizeSearchMetadata(props.modelValue));
    const savedValues = computed(() => normalizeSearchMetadata(props.originalValues));
    const currentKeys = computed(() => new Set(currentValues.value.map(comparisonKey)));
    const savedKeys = computed(() => new Set(savedValues.value.map(comparisonKey)));
    const pendingKeys = computed(() => new Set(pendingValues.value.map(comparisonKey)));
    const visibleKeys = computed(() => new Set([
      ...currentKeys.value,
      ...pendingKeys.value
    ]));

    const canAdd = computed(() =>
      normalizeSearchMetadata(entryText.value).length > 0 ||
      pendingValues.value.length > 0
    );
    const canonicalSuggestions = computed(() => normalizeSearchMetadata(props.suggestions));
    const suggestionByKey = computed(() => new Map(
      canonicalSuggestions.value.map(suggestion => [comparisonKey(suggestion), suggestion])
    ));
    const isNewCatalogValue = value => {
      const key = comparisonKey(value);

      return props.highlightNewValues &&
        !suggestionByKey.value.has(key) &&
        !savedKeys.value.has(key);
    };

    const filteredSuggestions = computed(() => {
      if (entryText.value.includes(",") || entryText.value.includes("\n")) { return []; }

      const query = comparisonKey(entryText.value);

      return canonicalSuggestions.value
        .filter(suggestion => {
          const key = comparisonKey(suggestion);
          return !visibleKeys.value.has(key) && (!query || key.includes(query));
        })
        .sort((first, second) => {
          const firstKey = comparisonKey(first);
          const secondKey = comparisonKey(second);
          const firstStartsWith = query && firstKey.startsWith(query);
          const secondStartsWith = query && secondKey.startsWith(query);

          if (firstStartsWith !== secondStartsWith) { return firstStartsWith ? -1 : 1; }
          return 0;
        })
        .slice(0, 10);
    });

    const pendingEntries = computed(() => normalizeSearchMetadata(entryText.value));
    const pendingNewEntries = computed(() => pendingEntries.value.filter(
      entry => isNewCatalogValue(entry)
    ));
    const selectedNewEntries = computed(() => currentValues.value.filter(
      entry => isNewCatalogValue(entry)
    ));
    const stagedNewEntries = computed(() => pendingValues.value.filter(
      entry => isNewCatalogValue(entry)
    ));
    const allNewEntries = computed(() => normalizeSearchMetadata([
      ...selectedNewEntries.value,
      ...stagedNewEntries.value,
      ...pendingNewEntries.value
    ]));
    const highlightedExistingSuggestion = computed(() =>
      suggestionsOpen.value &&
      highlightedSuggestionIndex.value >= 0 &&
      Boolean(filteredSuggestions.value[highlightedSuggestionIndex.value])
    );
    const isCreatingNewValue = computed(() =>
      props.confirmNewValues &&
      allNewEntries.value.length > 0
    );
    const isPendingNewValue = computed(() =>
      stagedNewEntries.value.length > 0 || (
        pendingNewEntries.value.length > 0 &&
        !highlightedExistingSuggestion.value
      )
    );
    const entryButtonLabel = computed(() =>
      isPendingNewValue.value
        ? props.newValueButtonLabel
        : props.addButtonLabel
    );
    const newValueAlert = computed(() =>
      allNewEntries.value.length === 1
        ? `You have added a new ${props.itemName}. Are you sure you want to create it?`
        : `You have added new ${itemNamePlural.value}. Are you sure you want to create them?`
    );

    const pills = computed(() => {
      const stagedPills = pendingValues.value.map(value => ({
        value,
        key: comparisonKey(value),
        status: isNewCatalogValue(value) ? "new" : "current",
        pending: true
      }));

      if (props.creatorMode) {
        const currentPills = currentValues.value.map(value => ({
          value,
          key: comparisonKey(value),
          status: isNewCatalogValue(value) ? "new" : "current"
        }));

        return [...currentPills, ...stagedPills];
      }

      const savedPills = savedValues.value.map(value => ({
        value,
        key: comparisonKey(value),
        status: currentKeys.value.has(comparisonKey(value)) ? "current" : "removed"
      }));

      const addedPills = currentValues.value
        .filter(value => !savedKeys.value.has(comparisonKey(value)))
        .map(value => ({
          value,
          key: comparisonKey(value),
          status: isNewCatalogValue(value) ? "new" : "current"
        }));

      return [...savedPills, ...addedPills, ...stagedPills];
    });

    const updateValues = values => {
      emit("update:modelValue", normalizeSearchMetadata(values));
    };

    const addEntries = () => {
      const additions = normalizeSearchMetadata(entryText.value).map(
        entry => suggestionByKey.value.get(comparisonKey(entry)) ?? entry
      );
      const valuesToAdd = normalizeSearchMetadata([
        ...pendingValues.value,
        ...additions
      ]);
      if (!valuesToAdd.length) { return; }

      updateValues([...currentValues.value, ...valuesToAdd]);
      pendingValues.value = [];
      entryText.value = "";
      suggestionsOpen.value = false;
    };

    const handleInput = event => {
      const inputValue = String(event.target?.value ?? entryText.value);

      if (!inputValue.includes(",") && !inputValue.includes("\n")) {
        openSuggestions();
        return;
      }

      const segments = inputValue.split(/[\n,]/);
      const trailingValue = segments.pop() ?? "";
      const completedEntries = normalizeSearchMetadata(segments).map(
        entry => suggestionByKey.value.get(comparisonKey(entry)) ?? entry
      );

      pendingValues.value = normalizeSearchMetadata([
        ...pendingValues.value,
        ...completedEntries
      ]).filter(entry => !currentKeys.value.has(comparisonKey(entry)));

      entryText.value = trailingValue.replace(/^\s+/, "");
      suggestionsOpen.value = true;
      highlightedSuggestionIndex.value = -1;
    };

    const selectSuggestion = suggestion => {
      pendingValues.value = normalizeSearchMetadata([
        ...pendingValues.value,
        suggestion
      ]).filter(entry => !currentKeys.value.has(comparisonKey(entry)));
      entryText.value = "";
      suggestionsOpen.value = false;
      highlightedSuggestionIndex.value = -1;
    };

    const openSuggestions = () => {
      suggestionsOpen.value = true;
      highlightedSuggestionIndex.value = -1;
    };

    const closeSuggestions = () => {
      suggestionsOpen.value = false;
    };

    const moveSuggestion = direction => {
      if (!suggestionsOpen.value) {
        openSuggestions();
        return;
      }

      const count = filteredSuggestions.value.length;
      if (!count) { return; }

      if (highlightedSuggestionIndex.value < 0) {
        highlightedSuggestionIndex.value = direction > 0 ? 0 : count - 1;
        return;
      }

      highlightedSuggestionIndex.value =
        (highlightedSuggestionIndex.value + direction + count) % count;
    };

    const handleEnter = () => {
      if (!canAdd.value) { return; }

      const suggestion = filteredSuggestions.value[highlightedSuggestionIndex.value];

      if (suggestionsOpen.value && suggestion) {
        selectSuggestion(suggestion);
        return;
      }

      addEntries();
    };

    const handlePill = pill => {
      if (pill.pending) {
        pendingValues.value = pendingValues.value.filter(
          value => comparisonKey(value) !== pill.key
        );
        return;
      }

      if (pill.status === "removed") {
        const restoredKeys = new Set([
          ...currentValues.value.map(comparisonKey),
          pill.key
        ]);
        const restoredSavedValues = savedValues.value.filter(value =>
          restoredKeys.has(comparisonKey(value))
        );
        const pendingAdditions = currentValues.value.filter(value =>
          !savedKeys.value.has(comparisonKey(value))
        );

        updateValues([...restoredSavedValues, ...pendingAdditions]);
        return;
      }

      updateValues(
        currentValues.value.filter(value => comparisonKey(value) !== pill.key)
      );
    };

    const pillTitle = pill => {
      if (pill.status === "removed") { return `Restore ${pill.value}`; }
      if (pill.status === "added") { return `Remove pending addition ${pill.value}`; }
      return `Remove ${pill.value}`;
    };

    return {
      entryText,
      inputId,
      suggestionListId,
      canAdd,
      pills,
      suggestionsOpen,
      filteredSuggestions,
      highlightedSuggestionIndex,
      itemNamePlural,
      isCreatingNewValue,
      entryButtonLabel,
      newValueAlert,
      handleInput,
      addEntries,
      selectSuggestion,
      openSuggestions,
      closeSuggestions,
      moveSuggestion,
      handleEnter,
      handlePill,
      pillTitle
    };
  }
};
</script>

<style scoped>
.metadata-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.metadata-field > label {
  color: #28342e;
  font-weight: 600;
}

.metadata-entry-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
}

.metadata-entry-container {
  position: relative;
}

.metadata-input-shell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  min-width: 0;
  min-height: 46px;
  box-sizing: border-box;
  padding: 5px 8px;
  border: 1px solid #d7ded9;
  border-radius: 9px;
  background: #ffffff;
}

.metadata-input-shell:focus-within {
  border-color: #18ab69;
  outline: 3px solid rgba(24, 171, 105, 0.12);
}

.metadata-suggestions {
  position: absolute;
  z-index: 20;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #d7ded9;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 12px 28px rgba(18, 62, 87, 0.16);
}

.metadata-suggestion {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 9px 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #28342e;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.metadata-suggestion:hover,
.metadata-suggestion.highlighted {
  background: rgba(24, 171, 105, 0.12);
}

.metadata-suggestion .material-icons-outlined {
  color: #18ab69;
  font-size: 1.05rem;
}

.suggestion-status {
  color: #748079;
  font-size: 0.78rem;
}

.suggestion-error {
  color: #a43b3b;
}

.new-value-alert {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 9px 11px;
  border: 1px solid #d8ccf2;
  border-radius: 8px;
  background: #f0ebff;
  color: #65479b;
  font-size: 0.8rem;
  font-weight: 650;
  line-height: 1.35;
}

.new-value-alert .material-icons-outlined {
  margin-top: 1px;
  font-size: 1rem;
}

.metadata-input-shell input {
  flex: 1 1 180px;
  min-width: 140px;
  box-sizing: border-box;
  padding: 7px 5px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #1f2a25;
  font: inherit;
}

.metadata-input-shell input:focus {
  outline: 0;
}

.enter-button {
  min-width: 88px;
  padding: 10px 16px;
  border: 0;
  border-radius: 9px;
  background: #123e57;
  color: #ffffff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.enter-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.field-hint {
  color: #748079;
  font-size: 0.82rem;
  font-weight: 400;
}

.metadata-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.metadata-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 7px 11px;
  border: 0;
  border-radius: 999px;
  color: #ffffff;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.15;
  text-align: left;
  cursor: pointer;
}

.pill-current {
  background: #18ab69;
}

.pill-added {
  background: #d49a18;
}

.pill-new {
  border: 1px solid #d8ccf2;
  background: #f0ebff;
  color: #65479b;
}

.pill-removed {
  background: #d64545;
}

.pill-symbol {
  font-size: 1rem;
  line-height: 0.8;
}

.pill-status {
  font-size: 0.95rem;
}

@media (max-width: 520px) {
  .metadata-entry-row {
    grid-template-columns: 1fr;
  }

  .enter-button {
    width: 100%;
  }

  .metadata-suggestions {
    right: 0;
  }
}
</style>
